/**
 * Reading something from the network, with the three states that implies.
 *
 * Every list and detail screen in this application needs the same four things:
 * the data, whether a request is in flight, whether the last one failed, and a
 * way to run it again. Written per screen it is fifteen lines that drift —
 * one forgets to clear the error before retrying, another leaves `loading`
 * true on failure, a third renders an empty state while the first request is
 * still going and shows "no results" to somebody who has results.
 *
 * Three properties are worth stating, because each is a bug this replaces:
 *
 * **A stale response never lands.** If the fetcher's identity changes while a
 * request is in flight — the user typed another character into the search box
 * — the earlier response is discarded rather than overwriting the newer one.
 * Without that, fast typing settles on whichever request happened to finish
 * last, which is not necessarily the one that matches what is on screen.
 *
 * **`loading` is true only while there is no data yet.** A refresh keeps the
 * previous rows on screen and reports itself through `refreshing`, so a
 * filter change does not blank the table and jump the page.
 *
 * **An error does not erase the data.** A failed refresh shows the previous
 * rows and the error together; throwing away what the user was reading
 * because a later request failed is the worse of the two outcomes.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface Resource<T> {
  data: T | null;
  /** No data yet and a request in flight: render a skeleton. */
  loading: boolean;
  /** Data on screen and a request in flight: render it, plus a hint. */
  refreshing: boolean;
  /** The last request failed. The data, if any, is what preceded it. */
  failed: boolean;
  error: unknown;
  /** Run it again. Safe to pass straight to a retry button. */
  reload: () => void;
}

/**
 * Fetch on mount and whenever `fetcher` changes.
 *
 * `fetcher` must be stable — wrap it in `useCallback` or `useMemo` at the call
 * site. An inline arrow function is a new identity on every render, which
 * turns this into an infinite request loop; that is the one way to misuse it,
 * and it is the same contract as `useEffect`'s dependency array.
 */
export function useResource<T>(fetcher: () => Promise<T>): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState<unknown>(null);

  // Incremented on every run. A response whose token is no longer current is
  // from a superseded request and is dropped.
  const token = useRef(0);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    const current = ++token.current;
    setPending(true);
    setError(null);

    void (async () => {
      try {
        const result = await fetcher();
        if (token.current !== current) return;
        setData(result);
      } catch (caught) {
        if (token.current !== current) return;
        // The data is deliberately left alone: a failed refresh should show
        // what was already read, next to the error.
        setError(caught);
      } finally {
        if (token.current === current) setPending(false);
      }
    })();
  }, [fetcher, nonce]);

  return {
    data,
    loading: pending && data === null,
    refreshing: pending && data !== null,
    failed: error !== null,
    error,
    reload,
  };
}
